import React, { useMemo } from "react";
import { Button } from "antd";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import ReactGA from "react-ga4";

import styles from "./ownAdPanel.scss";
import { AdStatus, LocalStorageKey, PageRoute } from "../../../models";
import { api, notification } from "../../../services";
import { getIconUrl } from "../../../services/asset";
import history from "../../../history";
import { EditIcon } from "../../MyAdsPage/MyAds/AdCard/AdCardActions/EditIcon";
import { useAdActions } from "../../MyAdsPage/MyAds/AdCard/hooks/useAdActions";
import { selectAdStatistics } from "../../../routes/Ad/ad.ducks";
import { usePostDeletionSurvey } from "../../../components/PostDeletionSurvey";

const formatNumber = (n: number) => (n || 0).toLocaleString("hr-HR");

const sumDaily = (series: any): number => {
  if (!Array.isArray(series)) return 0;
  return series.reduce((acc: number, d: any) => acc + (d && d.count ? d.count : 0), 0);
};

const statusLabel = (status: number) => {
  if (status === AdStatus.active) return { text: "Aktivan", dotClass: styles.dotActive };
  if (status === AdStatus.paused) return { text: "Pauziran", dotClass: styles.dotPaused };
  if (status === AdStatus.sold) return { text: "Prodano", dotClass: styles.dotSold };
  if (status === AdStatus.expired) return { text: "Istekli", dotClass: styles.dotExpired };
  return { text: "", dotClass: "" };
};

interface OwnAdPanelProps {
  adId: string;
  adStatus: number;
  module: string;
  category?: string;
  viewCount: number;
  favoriteCount: number;
  isXml: boolean;
  onRefetch: () => void;
}

const OwnAdPanel = ({ adId, adStatus, module, category, viewCount, favoriteCount, isXml, onRefetch }: OwnAdPanelProps) => {
  const adStatistics = useSelector(selectAdStatistics);
  const { showPostDeletionSurvey } = usePostDeletionSurvey();

  const phoneClickCount = useMemo(
    () => sumDaily(adStatistics && adStatistics.phoneClicks),
    [adStatistics]
  );

  const { adActions } = useAdActions({
    ad: { id: adId, module: module, status: adStatus },
    gaCategory: "Detalji oglasa",
    onEdit: (id: string) => {
      localStorage.removeItem(LocalStorageKey.savedForm);
      history.push(`${PageRoute.editAd}/${adId}`);
    },
    onDelete: (id: string) => {
      api.Ads.deleteAds(id).then(() => {
        notification.show("Oglas je obrisan.");
        history.push(PageRoute.myAd);

        // The survey modal is mounted outside the page tree, so it stays open after navigating away
        showPostDeletionSurvey(id, category);
      }).catch(() => {
        notification.showError("Brisanje oglasa nije uspjelo.");
      });
    },
    onSold: (id: string) => {
      api.Ads.sold(id).then(() => {
        notification.show("Oglas je označen kao prodan.");
        onRefetch();
      }).catch(() => {
        notification.showError("Označavanje oglasa kao prodanog nije uspjelo.");
      });
    },
    onPaused: (id: string) => {
      api.Ads.pause(id).then(() => {
        notification.show("Oglas je pauziran.");
        onRefetch();
      }).catch(() => {
        notification.showError("Pauziranje oglasa nije uspjelo.");
      });
    },
    onActive: (id: string) => {
      api.Ads.active(id).then(() => {
        notification.show("Oglas je aktiviran.");
        onRefetch();
      }).catch(() => {
        notification.showError("Aktivacija oglasa nije uspjela.");
      });
    },
    onRenew: (id: string) => {
      api.Ads.renew(id).then(() => {
        notification.show("Oglas je obnovljen.");
        onRefetch();
      }).catch(() => {
        notification.showError("Obnova oglasa nije uspjela.");
      });
    },
  });

  // XML ads are managed entirely through the sync feed, so all manipulation
  // buttons are hidden — statuses will be overwritten by the next sync anyway.
  const canEdit = !isXml && adStatus !== AdStatus.sold;
  const canDelete = !isXml;
  const canMarkSold = !isXml && adStatus !== AdStatus.sold;
  const canPause = !isXml && adStatus === AdStatus.active;
  // Paused ads take the /activate endpoint, expired ads need /renew — BE
  // rejects activate on expired with "Only paused ads can be activated".
  const canReactivate = !isXml && adStatus === AdStatus.paused;
  const canRenew = !isXml && adStatus === AdStatus.expired;
  const status = statusLabel(adStatus);

  const handleStatsClick = () => {
    ReactGA.event({
      category: "Detalji oglasa",
      action: "click",
      label: "Pogledaj detaljnu statistiku"
    });
  };

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <span className={styles.headerTitle}>{"Tvoj oglas"}</span>
        {status.text && (
          <span className={styles.statusLabel}>
            <span className={`${styles.statusDot} ${status.dotClass}`} />
            <span>{status.text}</span>
          </span>
        )}
      </div>

      <div className={styles.statsSection}>
        <div className={styles.statsTitle}>{"Statistika"}</div>
        <div className={styles.statsRow}>
          <div className={styles.stat}>
            <span className={styles.statValue}>{formatNumber(viewCount)}</span>
            <span className={styles.statLabel}>{"Pregleda"}</span>
          </div>
          <div className={styles.stat}>
            <span className={styles.statValue}>{formatNumber(phoneClickCount)}</span>
            <span className={styles.statLabel}>{"Klikova na telefon"}</span>
          </div>
          <div className={styles.stat}>
            <span className={styles.statValue}>{formatNumber(favoriteCount)}</span>
            <span className={styles.statLabel}>{"Spremanja"}</span>
          </div>
        </div>
      </div>

      {canEdit && (
        <p className={styles.editHint}>
          {'Naslov, cijenu i sve podatke mijenjaš odabirom "Uredi oglas"'}
        </p>
      )}

      {canEdit && (
        <Button type="primary" className={styles.actionPrimary} onClick={adActions.onEdit}>
          <EditIcon className={styles.iconWhite} />
          {"Uredi oglas"}
        </Button>
      )}

      {(canDelete || canMarkSold) && (
        <div className={styles.actionsRow}>
          {canDelete && (
            <Button className={styles.actionSecondary} onClick={adActions.onDelete}>
              <img className={styles.iconInline} src={getIconUrl("trash_can.svg")} alt="" />
              {"Obriši oglas"}
            </Button>
          )}
          {canMarkSold && (
            <Button className={styles.actionSecondary} onClick={adActions.onSold}>
              <img className={styles.iconInline} src={getIconUrl("sold.svg")} alt="" />
              {"Prodano"}
            </Button>
          )}
        </div>
      )}

      {canPause && (
        <Button className={`${styles.actionSecondary} ${styles.actionFullWidth}`} onClick={adActions.onPaused}>
          <img className={styles.iconInline} src={getIconUrl("pause_outlined.svg")} alt="" />
          {"Pauziraj oglas"}
        </Button>
      )}

      {canReactivate && (
        <Button className={`${styles.actionSecondary} ${styles.actionFullWidth}`} onClick={adActions.onActive}>
          <img className={styles.iconInline} src={getIconUrl("play_outlined.svg")} alt="" />
          {"Aktiviraj oglas"}
        </Button>
      )}

      {canRenew && (
        <Button className={`${styles.actionSecondary} ${styles.actionFullWidth}`} onClick={adActions.onRenew}>
          <img className={styles.iconInline} src={getIconUrl("reload_grey.svg")} alt="" />
          {"Obnovi oglas"}
        </Button>
      )}

      <Link
        to={`${PageRoute.statistics}/${adId}`}
        className={styles.statsLink}
        onClick={handleStatsClick}
      >
        <img className={styles.iconInline} src={getIconUrl("statistics.svg")} alt="" />
        {"Pogledaj detaljnu statistiku"}
      </Link>
    </div>
  );
};

export default OwnAdPanel;



// WEBPACK FOOTER //
// ./src/containers/AdDetailsPage/OwnAdPanel/index.tsx